/**
 * FINAL PRODUCTION - Frameworth Auction Bid Tracker
 * Version: 3.0 - Shopify Plus + Meta Best Practices 2025
 * 
 * Location: theme/assets/auction-tracker.js
 * 
 * Features:
 * - Tracks auction bids to Meta/Facebook
 * - Full catalog compatibility
 * - Event deduplication
 * - Shopify Analytics integration
 */

(function() {
  'use strict';
  
  // ============ CONFIGURATION ============
  const CONFIG = {
    // Set to false in production for better performance
    debugMode: false,
    
    // DOM Selectors (match your Auction Plus modal)
    formId: 'bidModalForm',
    modalSelector: '.auction-modal',
    bidButtonSelector: 'button.confirm-place-bid[type="submit"]',
    
    // ID Strategy - Keep false unless you know you need variants
    // false = Use product ID (recommended for 99% of cases)
    // true = Use variant ID (only if your Meta catalog uses variants)
    useVariantId: false
  };
  
  // ============ UTILITIES ============
  function log(message, data) {
    if (CONFIG.debugMode || window.location.search.includes('debug=true')) {
      console.log(`[Frameworth Auction] ${message}`, data || '');
    }
  }
  
  function generateEventId() {
    // Unique ID for Meta event deduplication
    return `auction_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
  
  // ============ DATA EXTRACTION ============
  function getProductIds() {
    // Get product ID (Shopify's default catalog sync uses this)
    const productId = 
      document.querySelector('meta[property="product:id"]')?.content ||
      window.ShopifyAnalytics?.meta?.product?.id ||
      '';
    
    // Get variant ID (only needed for variant-level catalogs)
    const variantId = 
      document.querySelector('meta[property="product:variant"]')?.content ||
      document.querySelector('[name="id"]')?.value ||
      window.ShopifyAnalytics?.meta?.selectedVariantId ||
      '';
    
    // Clean IDs (remove Shopify GID format if present)
    const cleanProductId = productId.toString().replace('gid://shopify/Product/', '');
    const cleanVariantId = variantId.toString().replace('gid://shopify/ProductVariant/', '');
    
    // Choose which ID to use for Meta catalog
    const catalogId = CONFIG.useVariantId && cleanVariantId ? cleanVariantId : cleanProductId;
    
    log('Product IDs detected', {
      productId: cleanProductId,
      variantId: cleanVariantId,
      catalogId: catalogId,
      useVariant: CONFIG.useVariantId
    });
    
    return {
      productId: cleanProductId,
      variantId: cleanVariantId,
      catalogId: catalogId
    };
  }
  
  function getBidData() {
    const form = document.getElementById(CONFIG.formId);
    if (!form) {
      log('Form not found');
      return null;
    }
    
    // Extract bid amount
    const amountInput = form.querySelector('input[name="amount"]');
    const formattedInput = form.querySelector('input[name="formatted-amount"]');
    
    let bidAmount = 0;
    if (amountInput?.value) {
      bidAmount = parseFloat(amountInput.value);
    } else if (formattedInput?.value) {
      bidAmount = parseFloat(formattedInput.value.replace(/[^0-9.-]+/g, ''));
    }
    
    // Get auction details
    const auctionId = form.querySelector('input[name="auction_uid"]')?.value || '';
    const productTitle = document.getElementById('modal-title')?.textContent?.trim() || '';
    
    // Get starting price
    let startingPrice = null;
    const priceElements = document.querySelectorAll('.flex-col h5.text-2xl');
    priceElements.forEach(el => {
      const label = el.previousElementSibling;
      if (label?.textContent?.includes('Starting Price')) {
        startingPrice = parseFloat(el.textContent.replace(/[^0-9.-]+/g, ''));
      }
    });
    
    // Check if first bid
    const noBidsAlert = document.querySelector('[role="alert"] .font-bold');
    const isFirstBid = noBidsAlert?.textContent?.toLowerCase().includes('no bids') || false;
    
    // Get product IDs
    const ids = getProductIds();
    
    return {
      // Bid data
      bidAmount: bidAmount,
      auctionId: auctionId,
      productTitle: productTitle,
      startingPrice: startingPrice,
      isFirstBid: isFirstBid,
      
      // Product IDs
      productId: ids.productId,
      variantId: ids.variantId,
      catalogId: ids.catalogId,
      
      // Meta data
      currency: window.Shopify?.currency?.active || 'CAD',
      productUrl: window.location.href,
      productHandle: window.location.pathname.split('/').pop(),
      timestamp: Math.floor(Date.now() / 1000)
    };
  }
  
  // ============ META/FACEBOOK TRACKING ============
  function trackToMeta(data, eventId) {
    if (typeof fbq === 'undefined') {
      log('⚠️ Facebook Pixel not found. Ensure Facebook & Instagram app is installed.');
      return false;
    }
    
    try {
      // CRITICAL: Format for Meta catalog matching
      const contentIds = [data.catalogId.toString()];
      const contents = [{
        id: data.catalogId.toString(),
        quantity: 1,
        item_price: data.bidAmount
      }];
      
      // 1. CUSTOM CONVERSION - AuctionBidPlaced
      fbq('trackCustom', 'AuctionBidPlaced', {
        // Catalog matching (REQUIRED)
        content_ids: contentIds,
        content_type: 'product',
        contents: contents,
        
        // Value optimization
        value: data.bidAmount,
        currency: data.currency,
        
        // Product info
        content_name: data.productTitle,
        content_category: 'Auction',
        
        // Deduplication (REQUIRED for 2025)
        event_id: eventId,
        event_time: data.timestamp,
        event_source_url: data.productUrl,
        action_source: 'website',
        
        // Custom auction data
        custom_data: {
          auction_id: data.auctionId,
          starting_price: data.startingPrice,
          is_first_bid: data.isFirstBid,
          product_handle: data.productHandle,
          variant_id: data.variantId
        }
      }, {
        eventID: eventId // Backup deduplication
      });
      
      // 2. STANDARD EVENT - AddToCart (for Dynamic Ads)
      fbq('track', 'AddToCart', {
        content_ids: contentIds,
        content_type: 'product',
        contents: contents,
        value: data.bidAmount,
        currency: data.currency,
        content_name: data.productTitle,
        event_id: eventId + '_atc'
      }, {
        eventID: eventId + '_atc'
      });
      
      // 3. STANDARD EVENT - InitiateCheckout (for higher intent)
      if (data.bidAmount >= (data.startingPrice * 1.2)) {
        // Track as InitiateCheckout if bid is 20% above starting
        fbq('track', 'InitiateCheckout', {
          content_ids: contentIds,
          content_type: 'product',
          contents: contents,
          value: data.bidAmount,
          currency: data.currency,
          num_items: 1,
          event_id: eventId + '_checkout'
        });
      }
      
      log('✅ Tracked to Meta', {
        eventId: eventId,
        catalogId: data.catalogId,
        bidAmount: data.bidAmount
      });
      
      return true;
      
    } catch (error) {
      log('❌ Meta tracking error:', error);
      return false;
    }
  }
  
  // ============ SHOPIFY ANALYTICS ============
  function publishToShopify(data, eventId) {
    if (!window.Shopify?.analytics?.publish) {
      log('Shopify Analytics not available');
      return false;
    }
    
    const eventData = {
      // Core data
      bidAmount: data.bidAmount,
      currency: data.currency,
      productId: data.productId,
      variantId: data.variantId,
      catalogId: data.catalogId,
      productTitle: data.productTitle,
      productHandle: data.productHandle,
      
      // Auction data
      auctionId: data.auctionId,
      startingPrice: data.startingPrice,
      isFirstBid: data.isFirstBid,
      
      // Meta tracking data
      eventId: eventId,
      eventTime: data.timestamp,
      sourceUrl: data.productUrl
    };
    
    window.Shopify.analytics.publish('auction_plus:bid_placed', eventData);
    log('📤 Published to Shopify Analytics');
    
    return true;
  }
  
  // Track if event was already fired to prevent duplicates
  let eventInProgress = false;
  let lastEventTime = 0;
  
  // ============ MAIN HANDLER ============
  function handleBidSubmission(source) {
    // Prevent duplicate events from firing within 500ms
    const now = Date.now();
    if (eventInProgress || (now - lastEventTime) < 500) {
      log('⚠️ Duplicate event prevented', { source: source });
      return;
    }
    
    eventInProgress = true;
    lastEventTime = now;
    
    log('🎯 Bid submission detected', { source: source });
    
    const data = getBidData();
    
    if (!data || data.bidAmount <= 0) {
      log('Invalid bid data', data);
      eventInProgress = false;
      return;
    }
    
    if (!data.catalogId) {
      log('⚠️ WARNING: No product ID found for catalog matching');
    }
    
    const eventId = generateEventId();
    
    // Track to Meta/Facebook
    const metaSuccess = trackToMeta(data, eventId);
    
    // Publish to Shopify Analytics
    const shopifySuccess = publishToShopify(data, eventId);
    
    if (metaSuccess || shopifySuccess) {
      log('✅ Bid tracking complete', {
        meta: metaSuccess,
        shopify: shopifySuccess,
        eventId: eventId
      });
    }
    
    // Reset flag after small delay
    setTimeout(function() {
      eventInProgress = false;
    }, 1000);
  }
  
  // ============ INITIALIZATION ============
  function attachTracking() {
    const form = document.getElementById(CONFIG.formId);
    
    if (!form) {
      log('Form not found, will retry...');
      return false;
    }
    
    if (form.dataset.auctionTracking === 'true') {
      log('Tracking already attached');
      return true;
    }
    
    form.dataset.auctionTracking = 'true';
    
    // ONLY attach to form submit event
    // The button click will naturally trigger the form submit
    form.addEventListener('submit', function() {
      // Don't prevent default - let form submit naturally
      handleBidSubmission('form-submit');
    });
    
    // NO SEPARATE BUTTON LISTENER - This was causing duplicates!
    // The submit button naturally triggers the form submit event
    
    log('✅ Tracking attached to form (single listener)');
    return true;
  }
  
  function init() {
    log('Initializing Frameworth Auction Tracker v3.0');
    
    // Try to attach immediately
    if (attachTracking()) {
      return;
    }
    
    // Watch for modal appearance
    const observer = new MutationObserver(function() {
      if (document.getElementById(CONFIG.formId)) {
        attachTracking();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Test function
    window.testAuctionBid = function(amount) {
      const data = getBidData();
      if (data) {
        data.bidAmount = amount || 100;
        const eventId = generateEventId();
        console.log('🧪 TEST: Simulating bid', data);
        trackToMeta(data, eventId);
      }
    };
    
    // Diagnostic function
    window.checkCatalogSetup = function() {
      console.log('=== CATALOG DIAGNOSTIC ===');
      const ids = getProductIds();
      console.table({
        'Product ID': ids.productId,
        'Variant ID': ids.variantId,
        'Catalog ID (sent to Meta)': ids.catalogId,
        'Using Variant?': CONFIG.useVariantId
      });
      console.log('To switch to variant IDs, set CONFIG.useVariantId = true');
      console.log('========================');
    };
    
    log('Ready. Debug: ?debug=true | Test: testAuctionBid(250) | Check: checkCatalogSetup()');
  }
  
  // Start when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
})();